"use client";

import React, { useState } from "react";
import { ShieldAlert, CreditCard, ScrollText } from "lucide-react";
import RichText from "@/utils/richText";

interface IProps {
  paymentPolicy?: string;
  cancellationPolicy?: string;
  termsAndConditions?: string;
}

const CancellationPolicy: React.FC<IProps> = ({
  paymentPolicy,
  cancellationPolicy,
  termsAndConditions,
}) => {
  const [open, setOpen] = useState(true);


  const cancellationSteps = [
    { label: "15–30 Days", note: "30% Non-refundable" },
    { label: "10–15 Days", note: "75% Non-refundable" },
    { label: "Under 10 Days", note: "100% Non-refundable" },
  ];

  const paymentSteps = [
    { label: "45–30 Days", note: "75% of total cost" },
    { label: "Within 30 Days", note: "100% of total cost" },
  ];

  return (
    <section className="w-full" id="cancellationPolicy">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full pb-8 bg-white transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-lg shadow-gray-200">
            <ScrollText size={22} />
          </div>
          <div className="text-left">
            <h2 className="block text-lg font-black uppercase  text-gray-900 leading-none">
              Booking Policies
            </h2>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
              Payment & Cancellation Terms
            </p>
          </div>
        </div>
      </button>

      <div className="space-y-8 animate-in fade-in slide-in-from-top-2 duration-500">
        <div className="relative pl-6 border-l-2 border-rose-100">
          <div className="flex items-center gap-2 mb-6">
            <ShieldAlert size={18} className="text-rose-500" />
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
              Cancellation Schedule
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cancellationSteps.map((step, idx) => (
              <div
                key={idx}
                className="p-5 rounded-3xl bg-rose-50/40 border border-rose-100/50"
              >
                <span className="text-xs font-black uppercase text-rose-600 tracking-widest mb-1 block">
                  {step.label}
                </span>
                <p className="text-base font-bold text-gray-900 tracking-tight">
                  {step.note}
                </p>
              </div>
            ))}
          </div>
          {cancellationPolicy && (
            <RichText
              content={cancellationPolicy}
              className="mt-6 text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none prose-p:leading-relaxed"
            />
          )}
        </div>

        <div className="relative pl-6 border-l-2 border-secondary-100">
          <div className="flex items-center gap-2 mb-6">
            <CreditCard size={18} className="text-secondary-500" />
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
              Payment Milestone
            </h3>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            {paymentSteps.map((step, idx) => (
              <div
                key={idx}
                className="flex-1 p-5 rounded-3xl bg-secondary-50/40 border border-secondary-100/50"
              >
                <span className="text-xs font-black uppercase text-secondary-600 tracking-widest mb-1 block">
                  {step.label}
                </span>
                <p className="text-base font-bold text-gray-900 tracking-tight">
                  {step.note}
                </p>
              </div>
            ))}
          </div>
          {paymentPolicy && (
            <RichText
              content={paymentPolicy}
              className="mt-6 text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none prose-p:leading-relaxed"
            />
          )}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-base font-bold uppercase text-gray-900">
              Standard Terms & Conditions
            </h3>
          </div>

          <RichText
            content={termsAndConditions || ""}
            className="text-sm text-gray-500 leading-relaxed prose prose-sm max-w-none 
                [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-2"
          />
        </div>
      </div>
    </section>
  );
};

export default CancellationPolicy;
